/* eslint-disable @typescript-eslint/consistent-type-assertions */
import type { AccountResource, AmazonWebServicesAccessKeyAccountResource, AmazonWebServicesOidcAccountResource, AzureEnvironment, AzureOidcAccountResource, AzureServicePrincipalAccountResource, AzureSubscriptionAccountResource, EnvironmentV2Resource, GenericOidcAccountResource, GoogleCloudAccountResource, SshKeyPairAccountResource, TenantResource, TokenAccountResource, UsernamePasswordAccountResource, } from "@octopusdeploy/octopus-server-client";
import { AccountType, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, Permission } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import type { AnalyticTrackedActionDispatcher } from "~/analytics/Analytics";
import { useCorrelationId, useAnalyticTrackedActionDispatch } from "~/analytics/Analytics";
import GenericOidcAccountEdit from "~/areas/infrastructure/components/AccountEdit/GenericOidcAccountEdit";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { repository } from "~/clientInstance";
import BaseComponent from "~/components/BaseComponent";
import FormPage from "~/components/FormPage/FormPage";
import { PageLoading } from "~/components/PageContent/PageLoading";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { ErrorPanel } from "~/components/form";
import type { AccountDrawerControlProps } from "./AccountEditBase";
import AmazonWebServicesAccountEdit from "./AmazonWebServicesAccountEdit";
import AzureAccountEdit from "./AzureAccountEdit";
import GoogleCloudAccountEdit from "./GoogleCloudAccountEdit";
import SshKeyPairAccountEdit from "./SshKeyPairAccountEdit";
import TokenAccountEdit from "./TokenAccountEdit";
import UsernamePasswordAccountEdit from "./UsernamePasswordAccountEdit";
interface NewAccountData {
    accountType: AccountType;
    environments: EnvironmentV2Resource[];
    tenants: TenantResource[];
    azureEnvironments: AzureEnvironment[];
}
interface AccountData extends NewAccountData {
    account: AccountResource;
}
export type AccountInitialData = NewAccountData | AccountData;
const AccountEditFormPage = FormPage<AccountInitialData>();
const Title = "Accounts";
const IsNew = "IsNew";
interface NewAccountEditProps {
    createNew: true;
    accountType: AccountType;
}
interface ExistingAccountEditProps {
    createNew: false;
    accountId: string;
}
export function AddOrEditAccountPage(props: NewAccountEditProps | ExistingAccountEditProps) {
    const trackAction = useAnalyticTrackedActionDispatch();
    const trackEvent = useTrackEvent();
    const correlationId = useCorrelationId();
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    return (<AccountEditFormPage title={Title} load={async () => {
            const environments = await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repository);
            const tenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
            const azureEnvironments = repository.Accounts.getIsolatedAzureEnvironments();
            if (!props.createNew) {
                const account = await repository.Accounts.get(props.accountId);
                return {
                    environments: await environments,
                    tenants: await tenants,
                    azureEnvironments: await azureEnvironments,
                    account,
                    accountType: account.AccountType,
                };
            }
            else {
                return {
                    environments: await environments,
                    tenants: await tenants,
                    azureEnvironments: await azureEnvironments,
                    accountType: props.accountType,
                };
            }
        }} key={props.createNew ? props.accountType : undefined} // Ensures the page can switch between account types through the global search
     renderWhenLoaded={(data) => <AccountEditInternal trackAction={trackAction} initialData={data} trackEvent={trackEvent} correlationId={correlationId} type="paper"/>} renderAlternate={() => <PageLoading loadingTitle={Title}/>}/>);
}
type AccountEditInternalProps = {
    initialData: AccountInitialData;
    trackAction: AnalyticTrackedActionDispatcher;
    trackEvent?: ReturnType<typeof useTrackEvent>;
    correlationId?: string;
};
type AccountEditInternalPaperProps = {
    type: "paper";
} & AccountEditInternalProps;
type AccountEditInternalDrawerProps = {
    type: "drawer";
} & AccountEditInternalProps & AccountDrawerControlProps;
export class AccountEditInternal extends BaseComponent<AccountEditInternalPaperProps | AccountEditInternalDrawerProps> {
    constructor(props: AccountEditInternalPaperProps | AccountEditInternalDrawerProps) {
        super(props);
    }
    hasAccount(check: object): check is AccountData {
        return (check as AccountData).account !== undefined;
    }
    render() {
        const data = this.props.initialData;
        const account = this.hasAccount(data) ? data.account : IsNew;
        const commonProps = {
            ...data,
            ...this.props,
        };
        switch (this.props.initialData.accountType) {
            case AccountType.SshKeyPair:
                return <SshKeyPairAccountEdit {...commonProps} account={account as SshKeyPairAccountResource}/>;
            case AccountType.Token:
                return <TokenAccountEdit {...commonProps} account={account as TokenAccountResource}/>;
            case AccountType.UsernamePassword:
                return <UsernamePasswordAccountEdit {...commonProps} account={account as UsernamePasswordAccountResource}/>;
            case AccountType.AzureSubscription:
            case AccountType.AzureOidc:
            case AccountType.AzureServicePrincipal:
                return <AzureAccountEdit {...commonProps} account={account as AzureServicePrincipalAccountResource | AzureSubscriptionAccountResource | AzureOidcAccountResource}/>;
            case AccountType.AmazonWebServicesAccount:
            case AccountType.AmazonWebServicesOidcAccount:
                return <AmazonWebServicesAccountEdit {...commonProps} account={account as AmazonWebServicesAccessKeyAccountResource | AmazonWebServicesOidcAccountResource}/>;
            case AccountType.GoogleCloudAccount:
                return <GoogleCloudAccountEdit {...commonProps} account={account as GoogleCloudAccountResource}/>;
            case AccountType.GenericOidcAccount:
                return <GenericOidcAccountEdit {...commonProps} account={account as GenericOidcAccountResource}/>;
            default:
                return <ErrorPanel message="Invalid path"/>;
        }
    }
    static displayName = "AccountEditInternal";
}
